import { Box, HStack, Link, PopoverPositioner, PopoverTrigger, Text, Portal, Popover as ChakraPopover } from "@chakra-ui/react"

import { IconButtonTypes, useIconButtonContent } from "../content/hooks/useButtonContent"
import { ActionButtonTypes, useActionButtonContent, ButtonContent } from "../content/hooks/useButtonContent"
export interface PopoverProps {
  children: React.ReactNode
  trigger?: React.ReactNode
  iconType?: IconButtonTypes
  actionType?: ActionButtonTypes
  actionVariant?: "primary" | "secondary"
  actionContent?: ButtonContent
  placement?: "right-end" | "left-end" | "top-end" | "bottom-end" | "top" | "bottom" | "left" | "right"
  loading?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  size?: "2xs" | "xs" | "sm" | "md" | "lg" | "xl" | "2xl"
  lazyMount?: boolean
  additionalContent?: string | React.ReactNode
  ariaLabel?: string
  width?: string
  minWidth?: string
  maxWidth?: string
  disabled?: boolean
  closeOnOutside?: boolean
  portalled?: boolean
  showArrow?: boolean
}

import { PopoverContent, PopoverRoot } from "@chakra-ui/react"
import { useState, useRef, useEffect } from "react"
import { ActionButton, IconButton } from "../content/components/Button"

const Popover = (props: PopoverProps) => {
  const {
    children,
    trigger,
    iconType,
    actionType,
    actionVariant,
    actionContent: defaultActionContent,
    placement = "bottom-end",
    loading,
    open,
    onOpenChange,
    size = "xs",
    lazyMount = false,
    additionalContent,
    ariaLabel,
    width = "fit-content !important",
    minWidth,
    maxWidth,
    disabled = false,
    closeOnOutside = false,
    portalled = true,
    showArrow = false,
  } = props
  const [openInternal, setOpenInternal] = useState(false)
  const ref = useRef<HTMLButtonElement>(null)
  let content = useIconButtonContent(iconType)
  let actionContent = useActionButtonContent(actionType)
  if (actionVariant && actionContent) actionContent.variant = actionVariant
  if (actionContent && defaultActionContent) actionContent = { ...actionContent, ...defaultActionContent }
  if (content && defaultActionContent) content = { ...content, ...(defaultActionContent as any) }
  useEffect(() => {
    if (ref.current) {
      if (openInternal || open) {
        ref.current.classList.add("hover")
      } else {
        ref.current.classList.remove("hover")
      }
    }
  }, [ref, open])

  const openChange = (e: { open: boolean }) => {
    if (!loading && !disabled) {
      onOpenChange && onOpenChange(e.open)
      open === undefined && setOpenInternal(e.open)
    }
  }

  return (
    <PopoverRoot
      positioning={{
        placement,
        gutter: showArrow ? 8 : 8,
        flip: true,
        shift: true,
        slide: true,
        arrowPadding: 20,
        overflowPadding: 12,
      }}
      open={open === undefined ? openInternal : open}
      onOpenChange={openChange}
      lazyMount={lazyMount}
      unmountOnExit={true}
      portalled={portalled}
      closeOnEscape={true}
      closeOnInteractOutside={closeOnOutside}
      variant="hydrologiq"
    >
      {trigger ? (
        <PopoverTrigger asChild disabled={disabled}>
          {trigger}
        </PopoverTrigger>
      ) : (
        <PopoverTrigger
          // @ts-ignore
          size={size}
          variant={content ? "hydrologiq_icon" : "hydrologiq_primary"}
          ref={ref}
          as={Box}
          css={{
            width: "fit-content",
            "& svg": {
              color: "#545454",
            },
            "&[data-state='open'] > div  > svg": {
              color: "white",
              bg: "#0bc5ea",
            },
          }}
          aria-label={ariaLabel || content?.label}
          disabled={disabled}
        >
          <HStack width="100%" justifyContent="space-between" height="15px">
            {additionalContent && typeof additionalContent === "string" ? (
              disabled ? (
                <Text color="gray.400">{additionalContent}</Text>
              ) : (
                <Link variant={"secondary"}>{additionalContent}</Link>
              )
            ) : (
              additionalContent
            )}

            {actionContent ? (
              <ActionButton size={size} context={actionContent} />
            ) : content ? (
              <IconButton size={size} context={content} />
            ) : undefined}
          </HStack>
        </PopoverTrigger>
      )}
      <Portal disabled={!portalled}>
        <PopoverPositioner zIndex={1500} width={width} minWidth={minWidth} maxWidth={maxWidth}>
          <PopoverContent
            width={width}
            minWidth={minWidth}
            maxWidth={maxWidth}
            css={{
              ...(width ? { width: `${width.replace(" !important", "")} !important` } : {}),
              ...(minWidth ? { minWidth: `${minWidth.replace(" !important", "")} !important` } : {}),
              ...(maxWidth ? { maxWidth: `${maxWidth.replace(" !important", "")} !important` } : {}),
            }}
            overflow="visible"
          >
            {showArrow && (
              <ChakraPopover.Arrow>
                <ChakraPopover.ArrowTip
                  borderColor="gray.300"
                  bg="var(--planner-colors-background, #F2F0EF)"
                />
              </ChakraPopover.Arrow>
            )}
            <Box
              width="100%"
              maxHeight="min(22.5rem, calc(100vh - 4rem))"
              overflowY="auto"
              overflowX="hidden"
              css={{
                "&::-webkit-scrollbar": { width: "0.375rem" },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0, 0, 0, 0.2)",
                  borderRadius: "0.1875rem",
                },
                "&::-webkit-scrollbar-track": {
                  backgroundColor: "transparent",
                },
              }}
            >
              {children}
            </Box>
          </PopoverContent>
        </PopoverPositioner>
      </Portal>
    </PopoverRoot>
  )
}

export default Popover
